import { Library, Search, Sparkles } from 'lucide-react';
import { useState, useCallback, forwardRef, useImperativeHandle, useEffect } from 'react';
import { Link } from 'react-router-dom';

import { AudioVisualizer } from '../components/AudioVisualizer';
import { AuthModal } from '../components/AuthModal';
import { RecordingTimer } from '../components/RecordingTimer';
import { SquareBirdCard } from '../components/SquareBirdCard';
import { useAuth } from '../contexts/AuthContext';
import { useAudioStream } from '../hooks/useAudioStream';
import { useBirdSocket } from '../hooks/useBirdSocket';
import { useDetectionPersistence } from '../hooks/useDetectionPersistence';
import { useUniqueBirdDetections } from '../hooks/useUniqueBirdDetections';
import { useUserStats } from '../hooks/useUserStats';
import { StreamConfig, LocationData } from '../types';
import { trackRecordingStart, trackRecordingStop, trackBirdDetection, trackBirdCardClick } from '../utils/analytics';
import { supabase } from '../utils/supabase';

interface MinimalHomeProps {
  setIsDetecting: (detecting: boolean) => void;
}

export interface MinimalHomeRef {
  startDetection: () => void;
  stopDetection: () => void;
}

export const MinimalHome = forwardRef<MinimalHomeRef, MinimalHomeProps>(function MinimalHome({ setIsDetecting: setGlobalIsDetecting }, ref) {
  const { user } = useAuth();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [isDetecting, setIsDetecting] = useState(false);
  const [detectionStartTime, setDetectionStartTime] = useState<number | null>(null);
  const [totalDetections, setTotalDetections] = useState(0);
  const { stats } = useUserStats();
  const [config] = useState<StreamConfig>({
    latitude: 12.9716, // Bangalore
    longitude: 77.5946,
    min_confidence: 0.1
  });
  
  const currentLocation: LocationData = {
    lat: config.latitude,
    lng: config.longitude,
    latitude: config.latitude,
    longitude: config.longitude,
    city: 'Bangalore',
    name: 'Bangalore'
  };
  
  const { isStreaming, audioData, startStream, stopStream } = useAudioStream();
  const { isConnected, detections, startDetection, stopDetection, sendAudioChunk } = useBirdSocket();
  const { 
    uniqueDetections, 
    getTotalUniqueSpecies, 
    getTotalDetectionEvents
  } = useUniqueBirdDetections(detections, currentLocation);
  
  const {
    createDetectionSession,
    updateSessionStats,
    updateUserLibrary,
    endDetectionSession,
    isAuthenticated
  } = useDetectionPersistence();

  const handleStartDetection = useCallback(async () => {
    try {
      if (!isConnected) {
        alert('Not connected to server. Please check if the API is running.');
        return;
      }
      
      if (isStreaming || isDetecting) {
        return;
      }
      
      const success = await startStream(sendAudioChunk);
      if (success) {
        startDetection(config);
        setIsDetecting(true);
        setGlobalIsDetecting(true);
        setDetectionStartTime(Date.now());
        
        // Track recording start
        trackRecordingStart();
        
        // Create session if authenticated
        if (isAuthenticated) {
          await createDetectionSession(currentLocation);
        }
      } else {
        alert('Please allow microphone access to listen for birds.');
      }
    } catch (error) {
      console.error('Error starting detection:', error);
      alert('Failed to start detection. Please try again.');
      setIsDetecting(false);
      setGlobalIsDetecting(false);
    }
  }, [isConnected, isStreaming, isDetecting, startStream, sendAudioChunk, startDetection, config, setGlobalIsDetecting]);
  
  const handleStopDetection = useCallback(async () => {
    try {
      stopStream();
      stopDetection();
      setIsDetecting(false);
      setGlobalIsDetecting(false);
      
      // Track recording stop
      if (detectionStartTime) {
        const duration = Math.round((Date.now() - detectionStartTime) / 1000);
        trackRecordingStop(duration, getTotalUniqueSpecies());
        setDetectionStartTime(null);
      }
      
      // End session and update library if authenticated
      if (isAuthenticated) {
        await updateSessionStats(getTotalDetectionEvents(), getTotalUniqueSpecies());
        await updateUserLibrary(new Map(uniqueDetections.map(d => [d.unique_key, d])));
        await endDetectionSession();
      }
    } catch (error) {
      console.error('Error stopping detection:', error);
      setIsDetecting(false);
      setGlobalIsDetecting(false);
    }
  }, [stopStream, stopDetection, isAuthenticated, updateSessionStats, updateUserLibrary, endDetectionSession, getTotalDetectionEvents, getTotalUniqueSpecies, uniqueDetections, setGlobalIsDetecting, detectionStartTime]);

  // Track new bird detections
  useEffect(() => {
    if (uniqueDetections && uniqueDetections.length > 0) {
      // Track each unique bird detected
      uniqueDetections.forEach((detection) => {
        if (detection.common_name && detection.max_confidence) {
          trackBirdDetection(detection.common_name, detection.max_confidence);
        }
      });
    }
  }, [uniqueDetections?.length]); // Only run when the count changes

  // Fetch total detections for the user
  useEffect(() => {
    async function fetchTotalDetections() {
      if (!user) {
        setTotalDetections(0);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('user_bird_library')
          .select('detection_count')
          .eq('user_id', user.id);

        if (error) {throw error;}

        const total = data?.reduce((sum, bird) => sum + bird.detection_count, 0) || 0;
        setTotalDetections(total);
      } catch (error) {
        console.error('Error fetching total detections:', error);
        setTotalDetections(0);
      }
    }

    fetchTotalDetections();
  }, [user]);

  // Cleanup detection when component unmounts
  useEffect(() => {
    return () => {
      if (isDetecting) {
        try {
          stopStream();
          stopDetection();
          setIsDetecting(false);
          setGlobalIsDetecting(false);
        } catch (error) {
          console.error('Cleanup error:', error);
        }
      }
    };
  }, [isDetecting, stopStream, stopDetection, setGlobalIsDetecting]);

  // Expose startDetection and stopDetection to parent via ref
  useImperativeHandle(ref, () => ({
    startDetection: handleStartDetection,
    stopDetection: handleStopDetection
  }), [handleStartDetection, handleStopDetection]);

  return (
    <div className="min-h-screen safe-area-top flex flex-col">
      {/* Animated Header - Only show when detecting */}
      <div className={`transition-all duration-500 ease-in-out overflow-hidden ${
        isDetecting 
          ? 'max-h-20 opacity-100' 
          : 'max-h-0 opacity-0'
      }`}>
        <nav className="flex items-center justify-between p-4 border-b border-neutral-200 dark:border-neutral-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-emerald-400 to-blue-500 rounded-xl flex items-center justify-center shadow-lg">
              <img 
                src="/app-icon-192.png" 
                alt="BirdSong" 
                className="w-8 h-8 rounded-lg object-cover"
                width="32"
                height="32"
                loading="eager"
              />
            </div>
            <div>
              <h1 className="text-lg font-bold text-neutral-900 dark:text-neutral-100">
                BirdSong
              </h1>
              <p className="text-xs text-neutral-500 dark:text-neutral-400">
                Your Birdsongs
              </p>
            </div>
          </div>
          
          {/* Stats Pills */}
          <div className="flex gap-2">
            <div className="bg-emerald-500/90 text-white px-3 py-1 rounded-full text-xs font-medium shadow-lg backdrop-blur-sm">
              {getTotalUniqueSpecies()} species
            </div>
            <div className="bg-blue-500/90 text-white px-3 py-1 rounded-full text-xs font-medium shadow-lg backdrop-blur-sm">
              {getTotalDetectionEvents()} detections
            </div>
          </div>
        </nav>
      </div>

      <div className="flex-1 flex flex-col pb-28">
        {!isDetecting ? (
          /* Homepage Content */
          <div className="flex-1 flex flex-col items-center justify-center p-6">
            {/* Logo and Branding - Center of Homepage */}
            <div className="text-center mb-12">
              <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-emerald-400 to-blue-500 rounded-2xl flex items-center justify-center shadow-xl">
                <img 
                  src="/app-icon-192.png" 
                  alt="BirdSong" 
                  className="w-16 h-16 rounded-xl object-cover"
                  width="64"
                  height="64"
                  loading="eager"
                />
              </div>
              <h1 className="text-4xl font-bold text-neutral-900 dark:text-neutral-100 mb-3">
                BirdSong
              </h1>
              <p className="text-lg text-neutral-600 dark:text-neutral-400 max-w-md mx-auto">
                Your personal birdsong collection
              </p>
            </div>

            {/* Secondary Actions - Pills for logged in users */}
            {user && (
              <div className="w-full max-w-sm mx-auto mb-8">
                <div className="flex gap-2 justify-center">
                  <Link 
                    to="/library"
                    className="bg-neutral-100 dark:bg-neutral-800 hover:bg-neutral-200 dark:hover:bg-neutral-700 px-4 py-2 rounded-full flex items-center gap-2 transition-colors text-sm font-medium text-neutral-700 dark:text-neutral-300"
                  >
                    <Library className="w-4 h-4" />
                    My Library
                  </Link>

                  <Link 
                    to="/search"
                    className="bg-neutral-100 dark:bg-neutral-800 hover:bg-neutral-200 dark:hover:bg-neutral-700 px-4 py-2 rounded-full flex items-center gap-2 transition-colors text-sm font-medium text-neutral-700 dark:text-neutral-300"
                  >
                    <Search className="w-4 h-4" />
                    Explore
                  </Link>
                </div>
              </div>
            )}

            {/* Tips */}
            <div className="w-full max-w-sm mx-auto mb-8">
              <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-lg">🐦</span>
                  </div>
                  <div>
                    <h3 className="font-medium text-sm mb-2 text-emerald-900 dark:text-emerald-100">
                      Tips
                    </h3>
                    <ul className="text-xs text-emerald-700 dark:text-emerald-300 space-y-1">
                      <li>• Find a quiet spot away from traffic</li>
                      <li>• Early morning has the most bird activity</li>
                      <li>• Keep still to avoid scaring birds</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Stats - Only for logged in users with data */}
            {user && stats && (stats.totalBirdsIdentified > 0 || stats.totalDetectionSessions > 0 || totalDetections > 0) && (
              <div className="w-full max-w-sm mx-auto">
                <div className="bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Sparkles className="w-4 h-4 text-yellow-500" />
                    <span className="font-medium text-sm">Your Progress</span>
                  </div>
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <div className="text-xl font-bold text-emerald-600 dark:text-emerald-400">{stats.totalBirdsIdentified}</div>
                      <div className="text-xs text-neutral-500 dark:text-neutral-400">Species</div>
                    </div>
                    <div>
                      <div className="text-xl font-bold text-blue-600 dark:text-blue-400">{totalDetections}</div>
                      <div className="text-xs text-neutral-500 dark:text-neutral-400">Detections</div>
                    </div>
                    <div>
                      <div className="text-xl font-bold text-purple-600 dark:text-purple-400">{stats.totalDetectionSessions}</div>
                      <div className="text-xs text-neutral-500 dark:text-neutral-400">Sessions</div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Auth Modal */}
            <AuthModal 
              isOpen={showAuthModal} 
              onClose={() => setShowAuthModal(false)} 
            />
          </div>
        ) : (
          /* Detection Interface */
          <div className="flex-1 flex flex-col relative">
            {/* Detected Birds Grid - Scrollable area */}
            <div className="flex-1 p-6 pb-0 overflow-y-auto">
              <div className="mb-6">
                <h3 className="font-medium mb-3">Detected Birds</h3>
                <div className="flex gap-3 overflow-x-auto scrollbar-hide pb-2">
                  <div className="grid grid-rows-3 grid-flow-col gap-3 auto-cols-max">
                    {/* Always show 9 cards minimum for consistent layout */}
                    {Array.from({ length: Math.max(9, uniqueDetections?.length || 0) }).map((_, index) => {
                      const detection = uniqueDetections?.[index];
                      
                      if (detection) {
                        return (
                          <SquareBirdCard
                            key={detection.unique_key}
                            detection={detection}
                            onClick={() => {
                              trackBirdCardClick(detection.common_name || 'Unknown');
                            }}
                          />
                        );
                      }
                      
                      return (
                        <div 
                          key={index}
                          className="flex-shrink-0 w-32 h-32 bg-neutral-100 dark:bg-neutral-800 border-2 border-dashed border-neutral-300 dark:border-neutral-600 rounded-xl flex items-center justify-center animate-pulse"
                        >
                          <div className="text-neutral-400 dark:text-neutral-600 text-xs text-center opacity-60">
                            <div className="w-6 h-6 mx-auto mb-2 rounded-full bg-neutral-300 dark:bg-neutral-600 animate-pulse" />
                            <span className="text-xs">{index + 1}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>

            {/* Fixed Visualizer - Hovering above bottom nav */}
            <div className="fixed bottom-28 left-4 right-4 z-10">
              <div className="bg-white/95 dark:bg-neutral-800/95 backdrop-blur-lg rounded-2xl p-4 shadow-lg border border-neutral-200/50 dark:border-neutral-700/50">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-3 h-3 rounded-full bg-emerald-500 animate-pulse" />
                  <span className="font-medium text-sm">Listening...</span>
                  <RecordingTimer 
                    startTime={detectionStartTime} 
                    className="text-emerald-600 dark:text-emerald-400 font-semibold"
                  />
                  <span className="text-xs text-neutral-500 dark:text-neutral-400 ml-auto">
                    {isConnected ? 'Connected' : 'Connecting...'}
                  </span>
                </div>
                <div className="h-16">
                  {audioData && <AudioVisualizer audioData={audioData} isActive={isStreaming} />}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
});